import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import Modal from 'react-modal';
import classNames from 'classnames';
import process from 'process';
import { useQuery } from 'react-query';
import { queryKeys } from '@/queries/queryKeys';
import axios from 'axios';
import { axiosAPI } from '@/services/axiosInstance/axipsInstansce';
import Step1 from '@/components/Modal/ConfirmationModalSteps/Step1';
import Step2 from '@/components/Modal/ConfirmationModalSteps/Step2';
import Loading from '@/components/Loading/Loading';
import { SVGClose } from '@/images/svg/svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export interface ConfirmationProps {
  onClose: any;
  isOpen: boolean;
  setEmail: Dispatch<SetStateAction<string>>;
  onSuccess: any;
  position: string;
  location: string[];
}
export default function ConfirmationModal({
  onClose,
  isOpen,
  setEmail,
  onSuccess,
  position,
  location,
}: ConfirmationProps) {
  const url = `https://api-us.storyblok.com/v2/cdn/stories/modals/mobile-email-confirmation?token=${process.env.NEXT_PUBLIC_STORYBLOK_API_KEY}&version=published`;
  const { data, isLoading } = useQuery(queryKeys.confirmationModal, () =>
    axios.get(url)
  );
  const story = data?.data?.story?.content;
  const [valuePhone, setValuePhone] = useState('');
  const [valueMail, setValueMail] = useState('');

  const [isPhoneSuccess, setIsPhoneSuccess] = useState(false);
  const [isMailSuccess, setIsMailSuccess] = useState(false);
  const [step, setStep] = useState(1);

  const [notValidPhone, setNotValidPhone] = useState(false);
  const [isLoadingVerify, setIsLoadingVerify] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const onVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      toast.error('Execute recaptcha not yet available', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      return;
    }

    await executeRecaptcha('enquiryFormSubmit').then((gReCaptchaToken: any) => {
      if (gReCaptchaToken) {
        submitEnquiryForm().then(() => {});
        return;
      }
    });
  }, [executeRecaptcha, valuePhone, valueMail]);

  async function submitEnquiryForm() {
    if (!!valuePhone.trim() && !!valueMail.trim()) {
      try {
        setIsLoadingVerify(true);
        await axiosAPI
          .post('verification/send-confirmation', {
            mobile: `+1${valuePhone}`,
            email: valueMail,
          })
          .then(() => {
            setIsLoadingVerify(false);
            setStep(step + 1);
          });
      } catch (error: any) {
        console.log(error);
        setIsLoadingVerify(false);
        if (error.response.status === 400) {
          setIsPhoneSuccess(false);
          setNotValidPhone(true);
        } else {
          setNotValidPhone(false);
        }
        if (error.response.status === 400) {
          onClose();
          toast.error(error.response.data.Details, {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
          });
        } else {
          setNotValidPhone(false);
        }
      }
    }
  }

  return (
    <>
      <Modal
        ariaHideApp={false}
        onRequestClose={onClose}
        isOpen={isOpen}
        closeTimeoutMS={100}
      >
        <div
          className={classNames(
            'content md:w-[416px] w-full',
            isLoadingVerify && 'pointer-events-none'
          )}
        >
          <div className='close' onClick={onClose}>
            <SVGClose />
          </div>
          {isLoadingVerify && <Loading />}
          {!isLoading && (
            <>
              <h4 className='p-4 text-xl pr-8'>{story.title}</h4>
              <div className='hor-line'></div>
              {step === 1 && (
                <Step1
                  story={story}
                  isPhoneSuccess={isPhoneSuccess}
                  setValuePhone={setValuePhone}
                  setValueMail={setValueMail}
                  setIsPhoneSuccess={setIsPhoneSuccess}
                  setIsMailSuccess={setIsMailSuccess}
                  valuePhone={valuePhone}
                  valueMail={valueMail}
                  isMailSuccess={isMailSuccess}
                  notValidPhone={notValidPhone}
                  setNotValidPhone={setNotValidPhone}
                />
              )}
              {step === 2 && (
                <Step2
                  valuePhone={valuePhone}
                  valueMail={valueMail}
                  isLoadingVerify={isLoadingVerify}
                  setIsLoadingVerify={setIsLoadingVerify}
                  setEmail={setEmail}
                  onSuccess={onSuccess}
                  position={position}
                  location={location}
                />
              )}
              <div className='hor-line'></div>
              <div
                className={classNames(
                  'flex gap-2 p-4',
                  step === 2
                    ? 'justify-center'
                    : 'md:justify-end justify-between'
                )}
              >
                <button
                  onClick={onClose}
                  className='button-close md:w-auto w-1/2'
                >
                  Close
                </button>
                {step === 1 && (
                  <button
                    type='submit'
                    className={classNames(
                      'button-next md:w-auto w-1/2',
                      isPhoneSuccess &&
                        isMailSuccess &&
                        !isLoadingVerify &&
                        'active'
                    )}
                    onClick={onVerify}
                  >
                    Verify
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
